import { useQuery } from '@apollo/client';
import type { FetchCustodianActivitiesFilterForGrid } from '@generated/graphql';
import { CustodianActivityStates } from '@generated/graphql';
import type {
  DataTableTanstackTable,
  PaginationState,
  RowSelectionState,
  RowsPerPageOption,
} from '@ovComponents/2-component/dataTable';
import { useDataTable } from '@ovComponents/2-component/dataTable';
import type { OnChangeFn } from '@tanstack/react-table';
import type { Theme } from 'hephaestus/core';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { buildActivitiesColumns } from '../table/activitiesGrid.columns';
import type { ActivitiesTableCardProps } from '../table/activitiesTableCard';
import {
  type ActivitiesGridActivity,
  FETCH_CUSTODIAN_ACTIVITIES_FOR_GRID,
  type FetchCustodianActivitiesForGridQuery,
} from '../utils/activities.queries';
import {
  buildFetchActivitiesGridVariables,
  shouldSuppressActivitiesGridResults,
} from '../utils/activitiesGridVariables';

type UseActivitiesGridInput = {
  enabled: boolean;
  theme: Theme;
  t: TFunction;
  orgId: string | undefined;
  gridFilter: FetchCustodianActivitiesFilterForGrid | null;
  state: CustodianActivityStates;
  page: number;
  setPage: (page: number) => void;
  pageSize: RowsPerPageOption;
  setPageSize: (size: RowsPerPageOption) => void;
  expandedActivityIds: ReadonlySet<string>;
  onToggleExpand: (activityId: string) => void;
  onRowClick: (row: ActivitiesGridActivity) => void;
};

export const useActivitiesGrid = ({
  enabled,
  theme,
  t,
  orgId,
  gridFilter,
  state,
  page,
  setPage,
  pageSize,
  setPageSize,
  expandedActivityIds,
  onToggleExpand,
  onRowClick,
}: UseActivitiesGridInput): {
  gridProps: Omit<ActivitiesTableCardProps, 'theme'>;
  visibleActivityIds: string[];
  refetch: () => Promise<unknown>;
} => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [resolvedGridFilterKey, setResolvedGridFilterKey] = useState<
    string | null
  >(null);

  const enableBulkSelection = state === CustodianActivityStates.Created;

  // biome-ignore lint/correctness/useExhaustiveDependencies: rows selected under a previous filter are no longer on screen
  useEffect(() => {
    setRowSelection({});
  }, [gridFilter]);

  const gridQueryVariables = useMemo(
    () =>
      buildFetchActivitiesGridVariables({
        gridFilter,
        pageSize,
        page,
      }),
    [gridFilter, page, pageSize],
  );
  const gridFilterKey = useMemo(() => JSON.stringify(gridFilter), [gridFilter]);

  const { loading, data, refetch, previousData } =
    useQuery<FetchCustodianActivitiesForGridQuery>(
      FETCH_CUSTODIAN_ACTIVITIES_FOR_GRID,
      {
        variables: gridQueryVariables,
        skip: !enabled || !orgId || !gridFilter,
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network',
      },
    );

  useEffect(() => {
    if (!loading && data) {
      setResolvedGridFilterKey(gridFilterKey);
    }
  }, [data, gridFilterKey, loading]);

  const suppressGridResults = shouldSuppressActivitiesGridResults(
    loading,
    gridFilterKey,
    resolvedGridFilterKey,
  );
  const sourceData = suppressGridResults
    ? undefined
    : (data?.fetchCustodianActivitiesForGrid ??
      previousData?.fetchCustodianActivitiesForGrid);
  const rows = sourceData?.custodianActivities ?? [];
  const totalRowCount = sourceData?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalRowCount / pageSize) || 1);

  const columns = useMemo(
    () =>
      buildActivitiesColumns(t, theme, {
        enableBulkSelection,
        expandedActivityIds,
        onToggleExpand,
      }),
    [enableBulkSelection, expandedActivityIds, onToggleExpand, t, theme],
  );

  const pagination = useMemo<PaginationState>(
    () => ({
      pageIndex: page - 1,
      pageSize,
    }),
    [page, pageSize],
  );

  const onPaginationChange = useCallback<OnChangeFn<PaginationState>>(
    (
      updater: PaginationState | ((prev: PaginationState) => PaginationState),
    ) => {
      const next =
        typeof updater === 'function' ? updater(pagination) : updater;
      if (next.pageSize !== pageSize) {
        setPageSize(next.pageSize as RowsPerPageOption);
        setPage(1);
        return;
      }
      setPage(next.pageIndex + 1);
    },
    [pagination, pageSize, setPage, setPageSize],
  );

  const { table } = useDataTable<ActivitiesGridActivity>({
    data: rows,
    columns,
    getRowId: (row: ActivitiesGridActivity) => row.id,
    enableRowSelection: enableBulkSelection,
    rowSelection,
    onRowSelectionChange: setRowSelection,
    paginationMode: 'manual',
    pageCount,
    pagination,
    onPaginationChange,
    sortingMode: 'manual',
    sorting: [],
  });

  const visibleActivityIds = useMemo(() => rows.map((row) => row.id), [rows]);

  const selectedCount = Object.values(rowSelection).filter(Boolean).length;

  const onSelectAllOnPage = useCallback(() => {
    const next: RowSelectionState = {};
    for (const row of table.getRowModel().rows) {
      if (row.getCanSelect()) {
        next[row.id] = true;
      }
    }
    setRowSelection(next);
  }, [table]);

  const onClearSelection = useCallback(() => {
    setRowSelection({});
  }, []);

  const onPerPageChange = useCallback(
    (perPage: number) => {
      setPageSize(perPage as RowsPerPageOption);
      setPage(1);
    },
    [setPage, setPageSize],
  );

  const gridProps: Omit<ActivitiesTableCardProps, 'theme'> = {
    table: table as DataTableTanstackTable<ActivitiesGridActivity>,
    pageSize,
    tableLoading: loading && !sourceData,
    pageCount,
    page,
    onPageChange: setPage,
    totalRowCount,
    onPerPageChange,
    onRowClick,
    expandedActivityIds,
    selectedCount,
    showBulkBar: enableBulkSelection,
    onSelectAllOnPage,
    onClearSelection,
  };

  return { gridProps, visibleActivityIds, refetch };
};
